import useDebouncedQueryParams from "./useDebouncedQueryParams";
import { useMemo } from "react";
import { AdvancedSearchParams } from "../components/dataBrowse/types";
import { ManifestType } from "../apiClient";

type DataBrowseQueryParams = {
  path: string;
  realAssetDeploymentPublicId: string;
  manifestType: ManifestType | "";
  search: string;
  fileTypes: string[] | string;
  dateModified: string;
  dateFrom: string;
  dateTo: string;
};

/**
 * Hook for the data browse page's search params.
 * The route's validateSearch (in routes/data/browse/index.tsx) owns the schema and defaults.
 * This hook adds derived state (activeFilters) and debounced search.
 */
export default () => {
  const { debouncedQueryParams, rawValues, setRawValues, queryParams } =
    useDebouncedQueryParams<DataBrowseQueryParams>(["search"], {
      path: "/",
      realAssetDeploymentPublicId: "",
      manifestType: "",
      search: "",
      fileTypes: [] as string[],
      dateModified: "",
      dateFrom: "",
      dateTo: "",
    });

  const searchQuery = rawValues.search || "";
  const debouncedSearchQuery = debouncedQueryParams.search || "";

  const activeFilters: AdvancedSearchParams | null = useMemo(() => {
    const ft = queryParams.fileTypes;
    const fileTypes = ft
      ? typeof ft === "string"
        ? ft.split(",").filter(Boolean)
        : ft
      : [];
    const dateModified = queryParams.dateModified || "any";
    const dateFrom = queryParams.dateFrom || "";
    const dateTo = queryParams.dateTo || "";

    const hasActiveFilters =
      fileTypes.length > 0 ||
      dateModified !== "any" ||
      dateFrom !== "" ||
      dateTo !== "";

    if (!hasActiveFilters) return null;

    return {
      searchTerm: queryParams.search || "",
      fileTypes,
      dateModified,
      dateFrom,
      dateTo,
    };
  }, [
    queryParams.search,
    queryParams.fileTypes,
    queryParams.dateModified,
    queryParams.dateFrom,
    queryParams.dateTo,
  ]);

  return {
    queryValues: rawValues,
    setQueryValues: setRawValues,
    queryParams,
    searchQuery,
    debouncedSearchQuery,
    activeFilters,
  };
};
